// My Trading Journey's shared state (JourneyCtx: the switch, the story, the Log window) and the Log
// window itself (expense, payout, account ended, a moment), the lessons from blown accounts and
// the expense side picker.
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api';
import { CATEGORIES, CATEGORY_NAMES, FIRMS, firmOf, isFunded, isProp } from './business';
import { BLOWN_CAUSES, END_CAUSE_NAMES, blownLessons, hasBothKinds } from './journey';
import { BEHAVIOR_CAUSE_NAMES } from './blownAccount';
import { dayKey, usd } from './pnl';
import { useI18n } from './i18n';

const EMPTY_STORY = { chapters: [], notes: [], moments: [] };
export const storyOf = (user) => ({ ...EMPTY_STORY, ...((user && user.journeyStory) || {}) });

// The Journey hub shares the Log window and the trader's story with every tab.
export const JourneyCtx = createContext({ openLog: null, story: EMPTY_STORY, saveStory: null, scope: 'all', setScope: null, allAccounts: [], allTrades: [], scopeExpenses: (list) => list });
export const useJourney = () => useContext(JourneyCtx);

// --- Real money vs prop accounts -------------------------------------------------------------

// --- One event on the story -------------------------------------------------------------------

// --- The month strip --------------------------------------------------------------------------

// --- A chapter --------------------------------------------------------------------------------

// --- The side rail ----------------------------------------------------------------------------

export function Lessons({ history, onEdit }) {
  const { t } = useI18n();
  const l = useMemo(() => blownLessons(history), [history]);
  const [open, setOpen] = useState(null);
  if (!l.count) return null;
  const max = Math.max(1, ...l.causes.map((c) => c.accounts.length));
  // The same lesson written on several accounts counts once, with how many accounts it cost.
  const lessons = Object.values(l.lessons.filter((h) => h.account.lesson).reduce((acc, h) => {
    const k = h.account.lesson.trim();
    (acc[k] = acc[k] || { text: k, count: 0 }).count += 1;
    return acc;
  }, {})).sort((a, b) => b.count - a.count);
  return (
    <section className="jo-card js-rail-card">
      <h2>{t('Lessons from blown accounts')}</h2>
      <p className="js-rail-sub">{t(l.count === 1 ? '{n} account blown. Why it ended:' : '{n} accounts blown. Why they ended:', { n: l.count })}</p>
      <ul className="js-causes">
        {l.causes.map((c) => (
          <li key={c.cause}>
            <button type="button" className="js-cause-row" aria-expanded={open === c.cause} onClick={() => setOpen(open === c.cause ? null : c.cause)}>
              <span>{t(END_CAUSE_NAMES[c.cause] || c.cause)}</span>
              <span className="jo-cat-bar"><b style={{ width: `${(c.accounts.length / max) * 100}%` }} /></span>
              <strong>{c.accounts.length}</strong>
            </button>
            {open === c.cause && (
              <ul className="js-cause-accounts">
                {c.accounts.map((h) => (
                  <li key={h.account._id}>
                    <button type="button" className="link-button" onClick={() => onEdit && onEdit({ accountId: h.account._id })}>{h.account.name}</button>
                    <span>{t(h.days === 1 ? '{n} day' : '{n} days', { n: h.days })}</span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
      {l.behaviors.length > 0 && (
        <>
          <h3 className="js-rail-h3">{t('What caused them')}</h3>
          <ul className="js-causes">
            {l.behaviors.map((c) => (
              <li key={c.cause}>
                <div className="js-cause-row">
                  <span>{t(BEHAVIOR_CAUSE_NAMES[c.cause] || c.cause)}</span>
                  <span className="jo-cat-bar"><b style={{ width: `${(c.accounts.length / Math.max(1, ...l.behaviors.map((x) => x.accounts.length))) * 100}%` }} /></span>
                  <strong>{c.accounts.length}</strong>
                </div>
                {c.cost < 0 && <p className="js-cause-cost">{t('Cost you {amount} in those accounts', { amount: usd.format(Math.abs(c.cost)) })}</p>}
              </li>
            ))}
          </ul>
        </>
      )}
      {l.lasting.length > 1 && (
        <>
          <h3 className="js-rail-h3">{t('Are you lasting longer?')}</h3>
          <ul className="js-lasting" aria-label={t('Days each blown account lasted, oldest first')}>
            {l.lasting.map((h) => {
              const most = Math.max(1, ...l.lasting.map((x) => x.days));
              return <li key={h.account._id} title={`${h.account.name}: ${t(h.days === 1 ? '{n} day' : '{n} days', { n: h.days })}`}><b style={{ height: `${Math.max(8, (h.days / most) * 100)}%` }} /></li>;
            })}
          </ul>
          {l.compared > 0 && (
            <p className={`js-rail-sub ${l.lastedNow > l.lastedThen ? 'gain' : l.lastedNow < l.lastedThen ? 'loss' : ''}`}>
              {t(l.compared === 1 ? 'Your first blown account lasted {then} days; your latest, {now} days.' : 'Your first {n} blown accounts lasted {then} days on average; your latest {n}, {now} days.', { n: l.compared, then: l.lastedThen, now: l.lastedNow })}
            </p>
          )}
        </>
      )}
      {lessons.length > 0 && (
        <>
          <h3 className="js-rail-h3">{t('What you learned')}</h3>
          <ul className="js-lessons">
            {lessons.slice(0, 6).map((x) => (
              <li key={x.text}><span>💡 {x.text}</span>{x.count > 1 && <em>{t('{n} accounts', { n: x.count })}</em>}</li>
            ))}
          </ul>
        </>
      )}
      {l.unknown.length > 0 && onEdit && (
        <div className="js-unknown">
          <p>{t(l.unknown.length === 1 ? '1 blown account has no reason yet.' : '{n} blown accounts have no reason yet.', { n: l.unknown.length })}</p>
          <button type="button" className="secondary small" onClick={() => onEdit({ accountId: l.unknown[0].account._id })}>{t('Add the reason')}</button>
        </div>
      )}
    </section>
  );
}

// --- The Story tab ----------------------------------------------------------------------------

// --- Log to your journey ----------------------------------------------------------------------

const LOG_TABS = [['expense', 'Expense', '🧾'], ['payout', 'Payout', '💵'], ['ended', 'Account ended', '💥'], ['moment', 'A moment', '📌']];
const FIRM_NAMES = FIRMS.map(([name]) => name);

function useDialog(onClose) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.focus();
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return ref;
}

// How an account ended so far, to fill the form when it's opened on an account.
function endedForm(account, today) {
  if (!account) return { accountId: '', end: today, outcome: 'failed', cause: 'daily_loss', note: '', lesson: '' };
  const end = account.attemptEndedAt ? dayKey(account.attemptEndedAt) : today;
  return {
    accountId: account._id, end,
    outcome: account.outcome === 'passed' ? 'passed' : account.endCause === 'went_live' ? 'went_live' : account.archived && !account.outcome ? 'closed' : 'failed',
    cause: account.endCause || 'daily_loss', note: account.attemptNote || '', lesson: account.lesson || '',
  };
}

// Which side of the Journey a cost counts on, for traders with both prop and personal accounts.
// Changing it clears the account and swaps a prop firm fee for market data on the personal side.
const EXPENSE_SIDES = [['prop', 'Prop firm accounts'], ['personal', 'Personal accounts'], ['general', 'Both (general cost)']];
const FIRM_FEES = ['evaluation', 'reset', 'activation', 'subscription'];
export const withSide = (side) => (x) => ({ ...x, side, accountId: '', category: side !== 'prop' && FIRM_FEES.includes(x.category) ? 'data' : x.category });
// The side a category usually belongs to: firm fees are prop, every other trading cost is personal.
export const sideOf = (category) => (FIRM_FEES.includes(category) ? 'prop' : 'personal');
export const sideAccounts = (accounts, side) => (side === 'general' ? [] : accounts.filter((a) => (side === 'personal') !== isProp(a)));
export function ExpenseSide({ value, onChange }) {
  const { t } = useI18n();
  return (
    <div className="exp-side">
      <span className="ended-label">{t('This was for')}</span>
      <div role="radiogroup" aria-label={t('This was for')}>
        {EXPENSE_SIDES.map(([id, label]) => (
          <button key={id} type="button" role="radio" aria-checked={value === id} className={`ended-cause${value === id ? ' on' : ''}`} onClick={() => onChange(id)}>{t(label)}</button>
        ))}
      </div>
    </div>
  );
}

export function JourneyLog({ mode = 'expense', preset = {}, accounts, story, saveStory, onSaved, onClose, onSessionExpired }) {
  const { t } = useI18n();
  const ref = useDialog(onClose);
  const today = dayKey(new Date());
  const [tab, setTab] = useState(mode);
  const prop = accounts.filter(isProp).sort((a, b) => Number(Boolean(a.archived)) - Number(Boolean(b.archived)) || String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
  const funded = prop.filter(isFunded);
  const presetAccount = accounts.find((a) => String(a._id) === String(preset.accountId));
  const m = preset.moment;
  const { scope } = useJourney();
  const both = hasBothKinds(accounts);
  const firstSide = preset.side || (presetAccount ? (isProp(presetAccount) ? 'prop' : 'personal') : scope === 'personal' ? 'personal' : 'prop');
  // The side follows the category until the trader picks one.
  const [sidePicked, setSidePicked] = useState(Boolean(preset.side || presetAccount));
  const [expense, setExpense] = useState({
    date: preset.date || today, amount: '', category: preset.category || (firstSide === 'prop' ? 'evaluation' : 'data'),
    firm: preset.firm || '', accountId: preset.accountId || '', note: '', side: both ? firstSide : 'prop',
  });
  const costAccounts = both ? sideAccounts(accounts, expense.side) : prop;
  const [payout, setPayout] = useState({ accountId: funded[0] ? funded[0]._id : '', date: today, amount: '', note: '' });
  const [ended, setEnded] = useState(() => endedForm(presetAccount, today));
  const [moment, setMoment] = useState({ date: m ? m.date : today, title: m ? m.title : '', text: m ? m.text : '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const field = (state, set) => (e) => set({ ...state, [e.target.name]: e.target.value });
  const chosen = accounts.find((a) => String(a._id) === String(ended.accountId));

  async function run(work) {
    setSaving(true);
    setError('');
    try {
      await work();
      onSaved(tab);
    } catch (err) {
      if (err.status === 401) return onSessionExpired && onSessionExpired();
      setError(err.message);
      setSaving(false);
    }
  }

  function save(e) {
    e.preventDefault();
    if (tab === 'expense') {
      if (!(Number(expense.amount) > 0)) return setError(t('Enter an amount.'));
      const account = accounts.find((a) => String(a._id) === String(expense.accountId));
      return run(() => api('/expenses', { method: 'POST', body: { ...expense, amount: Number(expense.amount), firm: expense.firm || (account ? firmOf(account) : ''), accountId: expense.accountId || undefined, side: both ? expense.side : undefined } }));
    }
    if (tab === 'payout') {
      if (!payout.accountId) return setError(t('Pick the funded account it came from.'));
      if (!(Number(payout.amount) > 0)) return setError(t('Enter an amount.'));
      return run(() => api(`/accounts/${payout.accountId}/cash`, { method: 'POST', body: { kind: 'withdrawal', amount: Number(payout.amount), date: payout.date, note: payout.note.trim() || t('Payout') } }));
    }
    if (tab === 'ended') {
      if (!chosen) return setError(t('Pick the account.'));
      return run(() => api(`/accounts/${chosen._id}`, {
        method: 'PATCH',
        body: {
          archived: true, attemptEndedAt: ended.end, outcome: ['failed', 'passed'].includes(ended.outcome) ? ended.outcome : '',
          endCause: ended.outcome === 'failed' ? ended.cause : ended.outcome === 'went_live' ? 'went_live' : '', attemptNote: ended.note.trim(), lesson: ended.lesson.trim(),
        },
      }));
    }
    if (!moment.title.trim()) return setError(t('Give the moment a title.'));
    const id = m ? m.id : `m-${Date.now().toString(36)}`;
    const next = { id, date: moment.date, title: moment.title.trim(), text: moment.text.trim() };
    return run(() => saveStory({ ...story, moments: [...story.moments.filter((x) => x.id !== id), next] }));
  }

  function removeMoment() {
    run(() => saveStory({ ...story, moments: story.moments.filter((x) => x.id !== m.id) }));
  }

  const editing = (tab === 'moment' && m) || (tab === 'ended' && presetAccount);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form ref={ref} tabIndex={-1} className="modal ja-modal js-log" onSubmit={save} role="dialog" aria-modal="true" aria-labelledby="jl-title">
        <div className="modal-header">
          <div>
            <h2 id="jl-title">{t('Log to your journey')}</h2>
            <p className="js-log-sub">{t('Everything you log shows up in your story, your money and your lessons.')}</p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
        </div>
        {!editing && (
          <div className="js-log-tabs" role="tablist">
            {LOG_TABS.map(([id, label, icon]) => (
              <button key={id} type="button" role="tab" aria-selected={tab === id} className={`js-log-tab${tab === id ? ' active' : ''}`} onClick={() => { setTab(id); setError(''); }}>
                <span aria-hidden="true">{icon}</span>{t(label)}
              </button>
            ))}
          </div>
        )}

        {tab === 'expense' && both && <ExpenseSide value={expense.side} onChange={(side) => { setSidePicked(true); setExpense(withSide(side)); }} />}
        {tab === 'expense' && (
          <div className="fields">
            <label>{t('Amount (USD)')}<input name="amount" type="number" min="0" step="any" value={expense.amount} onChange={field(expense, setExpense)} placeholder="165" autoFocus /></label>
            <label>{t('Date')}<input name="date" type="date" value={expense.date} onChange={field(expense, setExpense)} required /></label>
            <label>{t('What it was for')}
              <select name="category" value={expense.category}
                onChange={(e) => setExpense({ ...expense, category: e.target.value, ...(sidePicked || !both || expense.side === sideOf(e.target.value) ? {} : { side: sideOf(e.target.value), accountId: '' }) })}>{CATEGORIES.map((c) => <option key={c} value={c}>{t(CATEGORY_NAMES[c])}</option>)}</select>
            </label>
            {costAccounts.length > 0 && (
              <label>{t('Account (optional)')}
                <select name="accountId" value={expense.accountId} onChange={field(expense, setExpense)}>
                  <option value="">{t('Not for one account')}</option>
                  {costAccounts.map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
                </select>
              </label>
            )}
            <label>{t('Paid to (optional)')}
              <input name="firm" list="jl-firms" value={expense.firm} onChange={field(expense, setExpense)} maxLength={40} placeholder={t(expense.side === 'personal' ? 'e.g. Bookmap, CME data, TradingView' : 'e.g. Topstep, a course, a Discord group')} />
              <datalist id="jl-firms">{FIRM_NAMES.map((f) => <option key={f} value={f} />)}</datalist>
            </label>
            <label>{t('Note (optional)')}<input name="note" value={expense.note} onChange={field(expense, setExpense)} maxLength={200} /></label>
          </div>
        )}

        {tab === 'payout' && (funded.length ? (
          <div className="fields">
            <label>{t('Funded account')}
              <select name="accountId" value={payout.accountId} onChange={field(payout, setPayout)}>{funded.map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}</select>
            </label>
            <label>{t('Amount (USD)')}<input name="amount" type="number" min="0" step="any" value={payout.amount} onChange={field(payout, setPayout)} placeholder="800" autoFocus /></label>
            <label>{t('Date')}<input name="date" type="date" value={payout.date} onChange={field(payout, setPayout)} required /></label>
            <label>{t('Note (optional)')}<input name="note" value={payout.note} onChange={field(payout, setPayout)} maxLength={200} /></label>
          </div>
        ) : <p className="js-log-empty">{t('Payouts are logged on a funded account. Add your funded account first (Accounts page), or add a past one from the Overview tab.')}</p>)}

        {tab === 'ended' && (prop.length ? (
          <>
            <div className="fields">
              <label>{t('Account')}
                <select name="accountId" value={ended.accountId} onChange={(e) => setEnded(endedForm(accounts.find((a) => String(a._id) === e.target.value), today))} disabled={Boolean(presetAccount)}>
                  {!ended.accountId && <option value="">{t('Pick the account')}</option>}
                  {prop.map((a) => <option key={a._id} value={a._id}>{a.name}{a.archived ? ` · ${t('ended')}` : ''}</option>)}
                </select>
              </label>
              <label>{t('Ended on')}<input name="end" type="date" value={ended.end} onChange={field(ended, setEnded)} required /></label>
              <label>{t('How it ended')}
                <select name="outcome" value={ended.outcome} onChange={field(ended, setEnded)}>
                  <option value="failed">{t('Blown')}</option><option value="passed">{t('Passed')}</option>
                  {chosen && chosen.type === 'Funded' && <option value="went_live">{t('Moved to live')}</option>}
                  <option value="closed">{t('Closed')}</option>
                </select>
              </label>
              {ended.outcome === 'failed' && (
                <label>{t('Why it ended')}
                  <select name="cause" value={ended.cause} onChange={field(ended, setEnded)}>
                    {BLOWN_CAUSES.map((c) => <option key={c} value={c}>{t(END_CAUSE_NAMES[c])}</option>)}
                  </select>
                </label>
              )}
            </div>
            <label className="js-log-wide">{t('What happened')}
              <textarea name="note" value={ended.note} onChange={field(ended, setEnded)} maxLength={500} rows={3} placeholder={t('e.g. Lost it on one revenge day after two red trades.')} />
            </label>
            <label className="js-log-wide">{t('The lesson')}
              <input name="lesson" value={ended.lesson} onChange={field(ended, setEnded)} maxLength={300} placeholder={t('e.g. After 2 losses, the day is over.')} />
            </label>
            {chosen && !chosen.archived && <p className="js-log-hint">{t('It moves to your past accounts. Its trades and its story stay in your journey.')}</p>}
          </>
        ) : <p className="js-log-empty">{t('You have no prop accounts yet.')}</p>)}

        {tab === 'moment' && (
          <>
            <div className="fields">
              <label>{t('Title')}<input name="title" value={moment.title} onChange={field(moment, setMoment)} maxLength={80} autoFocus placeholder={t('e.g. Cancelled my last signal group')} /></label>
              <label>{t('Date')}<input name="date" type="date" value={moment.date} onChange={field(moment, setMoment)} required /></label>
            </div>
            <label className="js-log-wide">{t('What happened or what you learned (optional)')}
              <textarea name="text" value={moment.text} onChange={field(moment, setMoment)} maxLength={1000} rows={4} />
            </label>
          </>
        )}

        {error && <p className="error">{error}</p>}
        <div className="modal-actions">
          {tab === 'moment' && m && <button type="button" className="secondary danger js-log-delete" onClick={removeMoment} disabled={saving}>{t('Delete')}</button>}
          <button type="button" className="secondary" onClick={onClose}>{t('Cancel')}</button>
          <button type="submit" disabled={saving || (tab === 'payout' && !funded.length) || (tab === 'ended' && !prop.length)}>{saving ? t('Saving…') : t('Save')}</button>
        </div>
      </form>
    </div>
  );
}
